"use client";

import { Check, RotateCcw, TriangleAlert } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useMemo, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import { freeShippingThreshold } from "@/lib/bf-toolkit/calculations";

import { DEFAULTS, RANGES, STRINGS } from "./strings";

interface FreeShippingThresholdProps {
  /** Request locale, used only for currency formatting. */
  locale: string;
  /** ISO currency code for Intl formatting (e.g. "USD"). */
  currency: string;
  /** Currency symbol for the input prefix (e.g. "$"). */
  currencySymbol: string;
}

// Bump the number text well above the Input default so the figures a founder
// types read clearly. twMerge resolves the font-size conflict.
const INPUT_TEXT = "text-heading-20";

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/**
 * Parse a money string the way a founder might type it: strip currency symbols,
 * commas and stray characters, keep digits and a single decimal point. Returns
 * NaN for an empty/unparseable field so the UI can show a neutral prompt rather
 * than a bogus result.
 */
function parseMoney(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return Number.NaN;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** The rounding increment for a threshold, scaled to its magnitude. */
function niceIncrement(value: number): number {
  if (value < 100) return 5;
  if (value < 500) return 10;
  return 25;
}

/** Round a threshold UP to a clean increment — never down, so the rounded
 * threshold always still covers the shipping cost. */
function roundUpTo(value: number, increment: number): number {
  return Math.ceil(value / increment) * increment;
}

/**
 * The basket-growth figure, phrased for the headline. "only" is reserved for
 * genuinely modest jumps so it never reads absurdly ("only grow 210%"); the
 * figure is always a whole-number percentage.
 */
function upliftPhrase(upliftPct: number): { onlyPrefix: string; text: string } {
  const asPct = Math.round(upliftPct);
  return { onlyPrefix: asPct < 30 ? "only " : "", text: `${asPct}%` };
}

export function FreeShippingThreshold({
  locale,
  currency,
  currencySymbol,
}: FreeShippingThresholdProps) {
  const [aovStr, setAovStr] = useState<string>(String(DEFAULTS.aov));
  const [shippingStr, setShippingStr] = useState<string>(
    String(DEFAULTS.shipping)
  );
  const [marginPct, setMarginPct] = useState<number>(DEFAULTS.marginPct);
  // The user's own threshold. `null` means "follow the recommendation" — it
  // tracks the recommended value until the user drags the slider, then sticks.
  const [userThreshold, setUserThreshold] = useState<number | null>(null);

  const aov = parseMoney(aovStr);
  const shipping = parseMoney(shippingStr);

  const formatCurrency = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
    return (value: number) => nf.format(value);
  }, [locale, currency]);

  const state = useMemo(() => {
    // Need a real AOV and a real (possibly zero) shipping figure to say anything.
    if (!Number.isFinite(aov) || aov <= 0 || !Number.isFinite(shipping)) {
      return { kind: "empty" as const };
    }
    // Free shipping to you means you can offer it at any basket size.
    if (shipping <= 0) {
      return { kind: "zero-shipping" as const };
    }
    // No margin on the extra spend → no threshold can ever fund the shipping.
    if (marginPct <= 0) {
      return { kind: "no-margin" as const };
    }

    const { thresholdRaw, upliftPct } = freeShippingThreshold(
      aov,
      shipping,
      marginPct
    );
    const increment = niceIncrement(thresholdRaw);
    const recommended = roundUpTo(thresholdRaw, increment);
    // Recompute the headline uplift against the rounded threshold so the
    // "pays for itself" claim stays true.
    const recommendedUpliftPct = ((recommended - aov) / aov) * 100;

    // Explore-slider range: 0 → ~2× the recommendation, stepped on the same
    // increment so the thumb and the recommended tick line up cleanly.
    const sliderMax = roundUpTo(Math.max(recommended, aov) * 2, increment);

    return {
      kind: "ok" as const,
      thresholdRaw,
      recommended,
      recommendedUpliftPct,
      increment,
      sliderMax,
      upliftPct,
    };
  }, [aov, shipping, marginPct]);

  // Fire one analytics event ~800ms after the user stops adjusting, and only
  // when there's a real result. Raw money is intentionally not sent.
  const posthog = usePostHog();
  const upliftForAnalytics =
    state.kind === "ok" ? state.recommendedUpliftPct : null;
  const userAdjusted = userThreshold !== null;
  useEffect(() => {
    if (upliftForAnalytics === null) return;
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "free-shipping-threshold",
        marginPct,
        upliftPct: Math.round(upliftForAnalytics * 10) / 10,
        userAdjusted,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [posthog, marginPct, upliftForAnalytics, userAdjusted, userThreshold]);

  const isWarning = state.kind === "no-margin";

  return (
    <div className="border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
        {/* Inputs */}
        <div className="flex flex-col gap-5">
          <h2 className="text-heading-16 font-semibold text-[#0D0D0D]">
            {STRINGS.toolTitle}
          </h2>
          <MoneyField
            label={STRINGS.aov}
            value={aovStr}
            symbol={currencySymbol}
            onChange={setAovStr}
          />
          <MoneyField
            label={STRINGS.shipping}
            value={shippingStr}
            symbol={currencySymbol}
            onChange={setShippingStr}
          />
          <MarginField value={marginPct} onChange={setMarginPct} />
        </div>

        {/* Output */}
        <div aria-live="polite" className={cnPanel(isWarning)}>
          {state.kind === "empty" && (
            <p className="text-body-16 text-text-label">
              Enter your average order value and shipping cost to see your
              free-shipping threshold.
            </p>
          )}

          {state.kind === "zero-shipping" && (
            <p className="text-heading-24 lg:text-heading-32 text-[#0D0D0D]">
              Shipping costs you <span className="text-pulse-700">nothing</span>
              , so you can offer free shipping at any basket size. Add a
              shipping cost to find the threshold that pays for itself.
            </p>
          )}

          {state.kind === "no-margin" && (
            <div>
              <div className="flex items-start gap-3">
                <TriangleAlert
                  className="mt-1 size-6 shrink-0 text-red-600"
                  aria-hidden="true"
                />
                <p className="text-heading-24 lg:text-heading-32 text-red-700">
                  No threshold can pay for your shipping.
                </p>
              </div>
              <p className="text-body-16 mt-3 text-red-700">
                At {marginPct}% gross margin, topping up a basket earns you no
                extra profit, so there&rsquo;s nothing to offset the shipping
                you absorb. Raise your margin to find a workable threshold.
              </p>
            </div>
          )}

          {state.kind === "ok" && (
            <OkPanel
              aov={aov}
              shipping={shipping}
              marginPct={marginPct}
              state={state}
              userThreshold={userThreshold}
              onThresholdChange={setUserThreshold}
              formatCurrency={formatCurrency}
            />
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-3xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-7 w-auto shrink-0 md:h-8 md:w-auto"
        />
      </div>
    </div>
  );
}

// Result panel background: blue for a healthy result, red when there's no
// margin for any threshold to work.
function cnPanel(isWarning: boolean): string {
  const base =
    "flex min-h-[18rem] flex-col justify-center rounded-xl p-5 sm:p-6";
  return `${base} ${isWarning ? "bg-red-50" : "bg-pulse-50"}`;
}

interface OkState {
  thresholdRaw: number;
  recommended: number;
  recommendedUpliftPct: number;
  increment: number;
  sliderMax: number;
}

function OkPanel({
  aov,
  shipping,
  marginPct,
  state,
  userThreshold,
  onThresholdChange,
  formatCurrency,
}: {
  aov: number;
  shipping: number;
  marginPct: number;
  state: OkState;
  userThreshold: number | null;
  onThresholdChange: (v: number | null) => void;
  formatCurrency: (v: number) => string;
}) {
  const { recommended, recommendedUpliftPct, thresholdRaw, increment } = state;
  const sliderMax = Math.max(state.sliderMax, recommended + increment);

  // Active threshold: the user's if they've dragged, otherwise the
  // recommendation (which tracks the inputs live until they do).
  const active = clamp(userThreshold ?? recommended, 0, sliderMax);
  const touched = userThreshold !== null;

  const extraAtActive = Math.max(active - aov, 0);
  const marginOnExtra = extraAtActive * (marginPct / 100);
  // Coverage compares against the raw (unrounded) break-even, so a threshold
  // sitting between the raw figure and the rounded-up one still counts as
  // covered.
  const belowAov = active <= aov;
  const covers = !belowAov && active >= thresholdRaw;
  const gap = Math.max(shipping - marginOnExtra, 0);

  const { onlyPrefix, text } = upliftPhrase(recommendedUpliftPct);
  const tickPct = clamp((recommended / sliderMax) * 100, 4, 96);

  return (
    <div>
      <p className="text-heading-24 lg:text-heading-32 text-[#0D0D0D]">
        Consider setting free shipping at{" "}
        <span className="text-pulse-700">{formatCurrency(recommended)}</span>.
        <br />
        Baskets {onlyPrefix}need to grow{" "}
        <span className="text-pulse-700">{text}</span> to cover your{" "}
        {formatCurrency(shipping)} shipping cost.
      </p>

      {/* Explore: set your own threshold */}
      <div className="border-pulse-100 mt-6 border-t pt-5">
        <div className="mb-1 flex items-center justify-between gap-3">
          <span className="text-body-sm font-semibold text-[#0D0D0D]">
            {STRINGS.ownThreshold}
          </span>
          <span className="text-heading-16 font-semibold text-[#0D0D0D]">
            {formatCurrency(active)}
          </span>
        </div>

        {/* Recommended marker above the track */}
        <div className="relative h-5">
          <span
            className="text-label-11 text-pulse-700 absolute -translate-x-1/2 whitespace-nowrap"
            style={{ left: `${tickPct}%` }}
          >
            {STRINGS.recommended} {formatCurrency(recommended)}
          </span>
        </div>
        <div className="relative">
          <span
            aria-hidden="true"
            className="bg-pulse-700/60 absolute top-1/2 z-0 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${tickPct}%` }}
          />
          <SliderRoot
            aria-label={STRINGS.ownThreshold}
            min={0}
            max={sliderMax}
            step={increment}
            value={[active]}
            onValueChange={(vals) =>
              onThresholdChange(clamp(vals[0] ?? 0, 0, sliderMax))
            }
            rangeClassName="bg-pulse-700"
            className="py-2"
          >
            <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
          </SliderRoot>
        </div>

        {/* Coverage read-out */}
        <div className="mt-4 flex items-start gap-2">
          {covers ? (
            <Check
              className="mt-0.5 size-5 shrink-0 text-green-700"
              aria-hidden="true"
            />
          ) : (
            <TriangleAlert
              className="mt-0.5 size-5 shrink-0 text-orange-700"
              aria-hidden="true"
            />
          )}
          <p
            className={`text-body-14 ${covers ? "text-green-700" : "text-orange-700"}`}
          >
            <span className="font-semibold">
              {covers ? STRINGS.covered : STRINGS.notCovered}.
            </span>{" "}
            {belowAov ? (
              <>
                Most baskets already clear {formatCurrency(active)}, so
                you&rsquo;d give away shipping on orders that would have
                happened anyway.
              </>
            ) : covers ? (
              <>
                A basket topped up to {formatCurrency(active)} earns you{" "}
                {formatCurrency(marginOnExtra)} in extra margin, enough to cover
                your {formatCurrency(shipping)} shipping.
              </>
            ) : (
              <>
                A basket topped up to {formatCurrency(active)} earns only{" "}
                {formatCurrency(marginOnExtra)} in extra margin,{" "}
                {formatCurrency(gap)} short of your {formatCurrency(shipping)}{" "}
                shipping.
              </>
            )}
          </p>
        </div>

        {touched && (
          <button
            type="button"
            onClick={() => onThresholdChange(null)}
            className="text-body-14 text-pulse-700 mt-3 inline-flex items-center gap-1.5 font-medium transition-colors hover:text-blue-800"
          >
            <RotateCcw className="size-3.5" aria-hidden="true" />
            {STRINGS.reset}
          </button>
        )}
      </div>

      {/* Supporting figures for the active threshold */}
      <dl className="border-pulse-100 mt-5 flex flex-col gap-3 border-t pt-5">
        <SupportRow label={STRINGS.currentAov} value={formatCurrency(aov)} />
        <SupportRow
          label={STRINGS.extraSpend}
          value={formatCurrency(extraAtActive)}
        />
        <SupportRow
          label={STRINGS.marginOnExtra}
          value={formatCurrency(marginOnExtra)}
        />
        <SupportRow
          label={STRINGS.shippingCovered}
          value={formatCurrency(shipping)}
        />
      </dl>
    </div>
  );
}

function SupportRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-body-14 text-text-label">{label}</dt>
      <dd className="text-body-16 font-semibold text-[#0D0D0D]">{value}</dd>
    </div>
  );
}

function MoneyField({
  label,
  value,
  symbol,
  onChange,
}: {
  label: string;
  value: string;
  symbol: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <div className="relative">
        <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {symbol}
        </span>
        <Input
          id={id}
          inputMode="decimal"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-14 pl-9 ${INPUT_TEXT}`}
        />
      </div>
    </div>
  );
}

function MarginField({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const id = useId();
  const { min, max, step } = RANGES.margin;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label
          htmlFor={id}
          className="text-body-sm font-semibold text-[#0D0D0D]"
        >
          {STRINGS.margin}
        </label>
        <div className="relative">
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            aria-label={STRINGS.margin}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
            className={`h-14 w-24 [appearance:textfield] pr-8 text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${INPUT_TEXT}`}
          />
          <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
            %
          </span>
        </div>
      </div>
      <SliderRoot
        aria-label={STRINGS.margin}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(vals) => onChange(clamp(vals[0] ?? min, min, max))}
        rangeClassName="bg-pulse-700"
        className="py-2"
      >
        <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
      </SliderRoot>
    </div>
  );
}
